// pages/Login.jsx
import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { auth } from '../firebaseConfig';
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/Login.css';
import TitleSetter from '../components/TitleSetter';
import { rateLimiter, formatTimeUntilReset } from '../utils/rateLimiter';
import { initializeSession } from '../utils/sessionManager';
import { logAuthEvent, EVENT_TYPES } from '../utils/auditLogger';
import { handleError } from '../utils/errorHandler';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [pendingCredential, setPendingCredential] = useState(null);
  const [linkMessage, setLinkMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const ensureUserProfile = async (user) => {
    if (!user) return;
    const userRef = doc(db, `users/${user.uid}`);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      const [firstName = '', lastName = ''] = (user.displayName || '').split(' ');
      await setDoc(userRef, {
        firstName,
        lastName,
        ign: user.displayName || '',
        birthdate: '',
        email: user.email,
        role: 'player',
        teamId: null,
        createdAt: serverTimestamp(),
      });
    }
  };

  const postLoginRedirect = async (user) => {
    if (!user) return;
    if (!user.emailVerified) {
      alert('Please verify your email first.');
      navigate('/verify-email');
      return;
    }

    // Check if there's a returnTo path from BetaApprovedRoute
    const returnTo = location.state?.returnTo;

    const tokenResult = await user.getIdTokenResult();
    if (tokenResult?.claims?.superadmin) {
      // Superadmin: go to returnTo if exists, otherwise superadmin panel
      navigate(returnTo || '/superadmin');
      return;
    }

    const userRef = doc(db, `users/${user.uid}`);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      navigate('/onboarding');
    } else if (!userSnap.data().teamId) {
      // Redirect to returnTo if exists, otherwise team-decision
      navigate(returnTo || '/team-decision');
    } else {
      navigate('/my-strategy');
    }
  };

  const login = async (e) => {
    e.preventDefault();
    
    // Check rate limit
    const check = rateLimiter.checkLimit('LOGIN');
    if (!check.allowed) {
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('LOGIN'));
      alert(`Too many login attempts. Please try again in ${timeUntilReset}.`);
      return;
    }

    setLoading(true);
    try {
      // Record attempt
      if (!rateLimiter.record('LOGIN')) {
        const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('LOGIN'));
        alert(`Too many login attempts. Please try again in ${timeUntilReset}.`);
        setLoading(false);
        return;
      }

      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, email, password);
      const user = auth?.currentUser;
      await ensureUserProfile(user);
      
      // Initialize session management (with "Remember Me" option)
      const rememberMe = false; // TODO: Add "Remember Me" checkbox to UI
      initializeSession(rememberMe);
      
      // Log successful login
      await logAuthEvent(EVENT_TYPES.LOGIN_SUCCESS, 'User logged in successfully', {
        email: user?.email,
        rememberMe,
      });
      
      // Reset rate limit on successful login
      rateLimiter.reset('LOGIN');
      
      await postLoginRedirect(user);
    } catch (err) {
      // Use sanitized error handling
      const errorMessage = handleError(err, 'Login');
      
      // Log failed login attempt
      await logAuthEvent(EVENT_TYPES.LOGIN_FAILURE, 'Login attempt failed', {
        email,
        error: errorMessage,
      });
      
      // Don't reset rate limit on failure
      const check = rateLimiter.checkLimit('LOGIN');
      const remaining = check.remaining;
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('LOGIN'));
      
      if (remaining === 0) {
        alert(`Too many failed login attempts. Please try again in ${timeUntilReset}.`);
      } else {
        alert(`Login failed. Check your credentials. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // Check rate limit
    const check = rateLimiter.checkLimit('LOGIN');
    if (!check.allowed) {
      const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('LOGIN'));
      alert(`Too many login attempts. Please try again in ${timeUntilReset}.`);
      return;
    }

    setLoading(true);
    setLinkMessage('');
    try {
      // Record attempt
      if (!rateLimiter.record('LOGIN')) {
        const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('LOGIN'));
        alert(`Too many login attempts. Please try again in ${timeUntilReset}.`);
        setLoading(false);
        return;
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      await ensureUserProfile(user);
      
      // Reset rate limit on successful login
      rateLimiter.reset('LOGIN');
      
      await postLoginRedirect(user);
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const email = err.customData?.email || '';
        const cred = GoogleAuthProvider.credentialFromError(err);
        setLinkEmail(email);
        setPendingCredential(cred);
        setLinkMessage('We found an existing email/password account. Sign in once to link Google.');
      } else {
        alert(err.message || 'Google sign-in failed.');
      }
      console.error('Google sign-in error', err);
    } finally {
      setLoading(false);
    }
  };


  const handleLinkAccount = async (e) => {
    e.preventDefault();
    if (!pendingCredential || !linkEmail || !linkPassword) return;
    setLoading(true);
    try {
      const userCred = await signInWithEmailAndPassword(auth, linkEmail, linkPassword);
      await linkWithCredential(userCred.user, pendingCredential);
      await ensureUserProfile(userCred.user);
      setPendingCredential(null);
      setLinkPassword('');
      setLinkMessage('');
      await postLoginRedirect(userCred.user);
    } catch (err) {
      console.error('Link account error', err);
      alert(err.message || 'Failed to link Google. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TitleSetter title="Login" />
      <div className="login-container">
        <header className="login-topbar">
          <button className="login-brand" onClick={() => navigate('/landing')} aria-label="Back to TheBox">
            <img src="/assets/theboxlogo.png" alt="TheBox" />
          </button>
          <button className="back-site-btn" onClick={() => navigate('/landing')}>
            Back to site <span aria-hidden="true">&rarr;</span>
          </button>
        </header>

        <div className="login-wrapper">
          <div className="logo-side">
            <div className="login-brand-copy">
              <p className="login-eyebrow">Private team workspace</p>
              <h1>Built for contenders.</h1>
              <p>
                Your competitive workspace for cleaner prep, faster reviews, and better calls.
              </p>
              <h2>Plan. Brief. Scrim. Review. Adapt.</h2>
              <div className="login-proof-points">
                <span>Visual Playbooks</span>
                <span>Stats & Scouting</span>
                <span>Team Calendar</span>
              </div>
            </div>

            <div className="login-trust-note">
              <strong>Private team workspace.</strong>
              <span>Your playbooks, scouting, and stats stay within your team's workspace.</span>
            </div>
          </div>

          <div className="login-card">
            <div className="login-card-header">
              <p className="login-eyebrow">Sign in</p>
              <h1>Welcome back</h1>
              <p>Sign in to your team's workspace.</p>
            </div>

            <button
              type="button"
              className="social-btn"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <img src="/global%20logos/Google__G__logo.svg.png" alt="" className="social-icon" />
              Continue with Google
            </button>

            <div className="divider-row">
              <span />
              <p>OR</p>
              <span />
            </div>

            <form onSubmit={login} className="login-form">
              <label className="field-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div className="field-label-row">
                <label className="field-label" htmlFor="login-password">Password</label>
                <button type="button" className="inline-link" onClick={() => navigate('/recovery')}>
                  Forgot password?
                </button>
              </div>
              <div className="password-input-wrap">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button type="submit" className="login-submit-btn" disabled={loading}>
                {loading ? 'Signing in...' : <>Sign In <span aria-hidden="true">&rarr;</span></>}
              </button>
            </form>

            {pendingCredential && (
              <form className="link-box" onSubmit={handleLinkAccount}>
                <p className="link-text">
                  {linkMessage || 'Existing account found. Enter your password to link Google.'}
                </p>
                <input
                  type="password"
                  placeholder="Password"
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  required
                />
                <button type="submit" disabled={loading}>
                  {loading ? 'Linking...' : 'Link and continue'}
                </button>
              </form>
            )}

            <p className="register-text">
              Need access?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="register-link"
              >
                Join Private Beta <span aria-hidden="true">&rarr;</span>
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
